<template>
  <div
    @change="$emit('toggle')"
    @drop.prevent="onDrop"
    @dragstart.prevent
    @dragenter.prevent="onDragEnter"
    @dragleave.prevent="onDragLeave"
    @dragover.prevent="onDragOver"
  >
    <PositionWarning
      :show="showPositionWarning"
      :message="positionWarning"
      @dismiss="dismissWarning"
    />
    <div 
      ref="cropImage"
      :is-show="isCropMode"
    >
      <CropImage
        :is-show="isCropMode"
        :src="target.getSrc()"
        @onSave="(data) => onChange('newCrop', data)"
        @onCancel="isCropMode = false"
        @onAutoCrop="handleAutoCrop"
      />
    </div>

    <template v-if="options.isSelectImageFromBackground || options.isStaticImage">
      <div class="layout-sidebar-section">
        <div
          class="layout-sidebar-section-title"
          @click="$emit('toggleSection', 'themeSection')"
        >
          <strong>Image & Theme</strong>
          <b-icon :icon="isOpen.themeSection ? 'chevron-up' : 'chevron-down'" />
        </div>
        
        <transition>
          <b-collapse :visible="isOpen.themeSection">
            <div>
              <b-form-group
                class="mb-0"
              >
                <b-button
                  v-if="!options.isStaticImage"
                  variant="primary"
                  @click="imageSrc && !imageIsDefault ? onChange('selectBackground', 'Replace') : onChange('selectBackground')"
                >
                  <b-icon icon="cloud-upload" /> {{ `${imageSrc && !imageIsDefault ? 'Replace' : 'Select…'}` }}
                </b-button>
                <b-button
                  v-if="!options.isStaticImage"
                  v-b-tooltip.hover
                  title="Choose product image..."
                  variant="primary"
                  @click="toggleProductDropdown"
                >
                  <img
                    src="/img/icons/choose-product.png"
                    alt="choose-product-icon"
                    width="20"
                    height="20"
                  >
                </b-button>
                <span
                  v-if="imageSrc"
                  :href="imageSrc"
                  class="download-image my-2"
                  download
                  @click.stop
                >
                  <button
                    v-b-tooltip.hover.viewport.noninteractive="'Remove image'"
                    class="close-btn"
                    @click.stop="onChange('deleteConfirmationImage')"
                  >
                    <b-icon icon="x" />
                  </button>
                  <b-img
                    v-if="imagePreviewSrc"
                    :src="imagePreviewSrc"
                    class="img-preview"
                    fluid
                    rounded
                  />
                  <a
                    v-b-tooltip.hover.viewport.noninteractive="'Download'"
                    :href="imageSrc"
                    class="download-image-href"
                    download
                    @click.stop
                  >
                    <b-icon icon="download" />
                  </a>
                  <button
                    v-if="!options.isStaticImage"
                    v-b-tooltip.hover.viewport.noninteractive="'Edit image'"
                    class="edit-image-btn"
                    @click.stop="showImageEditorModal"
                  >
                    <b-icon icon="pencil-square" />
                  </button>
                </span>
              </b-form-group>
              <b-form-group>
                <b-form-checkbox
                  v-model="isClippingMask"
                  @change="onChange('setIsClippingMask', isClippingMask)"
                >
                  Create Clipping Mask
                </b-form-checkbox>
              </b-form-group>
              <b-form-group
                label="Theme"
                label-cols="3"
                invalid-feedback="No theme selected"
                :state="!!selectedTheme"
              >
                <Multiselect
                  v-model="selectedThemeObj"
                  :options="cThemesOptions"
                  label="name"
                  track-by="id"
                  placeholder="Select option"
                  @input="onChange('selectTheme', selectedTheme)"
                />
              </b-form-group>
            </div>
          </b-collapse>
        </transition>
      </div>
    </template>
    <div 
      v-if="options.isFileImage"
      class="layout-sidebar-section"
    >
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'productSettingsSection')"
      >
        <strong>Product Settings</strong>
        <b-icon :icon="isOpen.productSettingsSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.productSettingsSection">
          <div>
            <ProductImageDropdown
              ref="productImageDropdown"
              v-model="objectInput.fileIds"
              :is-file-image="options.isFileImage"
              :is-tab-active="isTabActive"
              @input="onChange('productImage', objectInput.fileIds)"
              @uploadInput="(fileIds) => onChange('productImage',{productIds: fileIds, type: 'uploaded_product_image'})"
            />
          </div>
        </b-collapse>
      </transition>
    </div>
    <template v-else>
      <ProductImageDropdown
        ref="productImageDropdown"
        v-model="objectInput.fileIds"
        :is-file-image="false"
        :is-tab-active="isTabActive"
        @input="onChange('productImage', objectInput.fileIds)"
        @uploadInput="(fileIds) => onChange('productImage',{productIds: fileIds, type: 'uploaded_product_image'})"
      />
    </template>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'borderSection')"
      >
        <strong>Border</strong>
        <b-icon :icon="isOpen.borderSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.borderSection">
          <div>
            <b-form-group
              label="Border color"
              label-cols="4"
            >
              <div class="d-flex align-items-center">
                <ColorPicker
                  :value="target.stroke"
                  :themes="themes"
                  copy
                  @input="onChange('strokeColor', $event)"
                />
                <b-button
                  v-b-tooltip.hover
                  variant="primary"
                  size="sm"
                  title="Use background color"
                  class="ms-2"
                  @click="onChange('strokeColor', target.fill)"
                >
                  <b-icon icon="back" />
                </b-button>
              </div>
            </b-form-group>
            <b-form-group
              v-if="options.isBackgroundThemeImage"
              label="Border width"
              :disabled="target.locked"
            >
              <b-input-group>
                <b-input
                  v-model="localStrokeWidth"
                  :min="0"
                  :max="maxStrokeWidth"
                  type="range"
                  @input="onChange('strokeWidth', localStrokeWidth)"
                />
                <template #append>
                  <b-input
                    v-model="localStrokeWidth"
                    :min="0"
                    :max="maxStrokeWidth"
                    type="number"
                    @input="onChange('strokeWidth', localStrokeWidth)"
                  />
                </template>
              </b-input-group>
            </b-form-group>
          </div>
        </b-collapse>
      </transition>
    </div>

    <div
      v-if="options.isUploadImage"
      class="layout-sidebar-section"
    >
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'imageSettingsSection')"
      >
        <strong>Image Settings</strong>
        <b-icon :icon="isOpen.imageSettingsSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.imageSettingsSection">
          <div>
            <b-form-group
              label="Image"
              label-cols="3"
            >
              <b-form-file
                placeholder="Choose a file"
                drop-placeholder="Drop file here"
                @input="onChange('upload', $event)"
              />
            </b-form-group>
          </div>
        </b-collapse>
      </transition>
    </div>

    <div
      v-if="options.isOutline"
      class="layout-sidebar-section"
    >
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'outlineSettingsSection')"
      >
        <strong>Outline Settings</strong>
        <b-icon :icon="isOpen.outlineSettingsSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.outlineSettingsSection">
          <div>
            <b-form-group
              label="Outline"
              label-cols="3"
            >
              <b-form-input
                v-model="objectInput.fileOutlineId"
                @change="onChange('productOutline', outlineData)"
              />
            </b-form-group>
            <b-form-group
              label="Color"
              label-cols="3"
            >
              <ColorPicker
                v-model="objectInput.fileOutlineColor"
                @input="onChange('productOutline', outlineData)"
              />
            </b-form-group>
          </div>
        </b-collapse>
      </transition>
    </div>
    <ObjectFillFit 
      v-if="target && (target.id.startsWith('bk_theme_img_') || target.id.startsWith('add_bk_theme_img_'))"
      :target="target" 
      :is-open="isOpen"
      @toggleSection="toggleSectionHandler"
    />

    <b-button
      v-show="options.isHtmlTable"
      class="mb-2"
      variant="success"
      @click="initTableEditor"
    >
      Edit Table
    </b-button>

    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'opacityAndScaleSection')"
      >
        <strong>Opacity & Scale</strong>
        <b-icon :icon="isOpen.opacityAndScaleSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.opacityAndScaleSection">
          <div
            @change.prevent.stop
          >
            <b-form-group label="Opacity">
              <b-input-group>
                <b-input
                  v-model="opacity"
                  min="0"
                  max="1"
                  step="0.01"
                  type="range"
                  @input="onChange('opacity', $event)"
                />
                <template #append>
                  <b-input
                    v-model="opacity"
                    min="0"
                    max="1"
                    step="0.01"
                    type="number"
                    @input="onChange('opacity', $event)"
                  />
                </template>
              </b-input-group>
            </b-form-group>
            <b-form-group
              :disabled="target.locked"
            >
              <template #label>
                <div class="d-flex justify-content-between">
                  <span>Scale</span>
                  <span>{{ percentOfTemplate }}% of Template</span>
                </div>
              </template>
              <b-input-group>
                <b-input
                  v-model="scale"
                  min="0"
                  max="10"
                  step="0.001"
                  type="range"
                  :disabled="isScaleDisabled"
                  @input="onChange('scale', $event)"
                />
                <template #append>
                  <b-input
                    v-model="scale"
                    min="0"
                    max="10"
                    step="0.001"
                    type="number"
                    :disabled="isScaleDisabled"
                    @input="onChange('scale', $event)"
                  />
                </template>
              </b-input-group>
            </b-form-group>
          </div>
        </b-collapse>
      </transition>
    </div>

    <div @change.prevent.stop>
      <PositionAndSize
        :x="parseFloat(objectInput.left)"
        :y="parseFloat(objectInput.top)"
        :h="parseFloat(objectInput.height)"
        :w="parseFloat(objectInput.width)"
        aspect-ratio-option:
        :is-scale-disabled="isScaleDisabled"
        :is-open="isOpen"
        @toggleSection="toggleSectionHandler"
        @input="onChange"
      />
    </div>

    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'actionSection')"
      >
        <strong>Actions</strong>
        <b-icon :icon="isOpen.actionSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.actionSection">
          <div>
            <b-form-row v-if="isRelatedFieldChangeAvailable">
              <b-col>
                <b-form-group
                  label="Related field"
                >
                  <b-form-select
                    :value="target.relatedField?.field"
                    :options="relatedTemplateFields"
                    @change="onChange('changeRelatedField', $event)"
                  />
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group label="Margin">
                  <b-form-input
                    :value="target.relatedField ? target.relatedField.margin : 5"
                    type="number"
                    min="0"
                    step="1"
                    @input="onChange('changeMargin', $event)"
                  />
                </b-form-group>
              </b-col>
            </b-form-row>

            <div class="d-flex justify-content-between align-items-start mb-3">
              <div>
                <b-form-checkbox
                  v-if="options.isFileImage"
                  v-model="objectInput.isAutoPosition"
                  class="mt-1 d-none"
                  size="sm"
                  @change="onChange('autoPosition', objectInput.isAutoPosition)"
                >
                  Auto position
                </b-form-checkbox>
              </div>
              <div class="image-editor-actions image-editor-actions--bg-actions mt-3">
                <button
                  v-if="options.isSelectImageFromBackground"
                  v-b-tooltip.hover
                  :title="resolveScaleImageAction('width').tooltip"
                  @click="onChange(resolveScaleImageAction('width').action, 'width')"
                >
                  <img src="/img/icons/expand.svg">
                </button>
                <button
                  v-if="options.isSelectImageFromBackground"
                  v-b-tooltip.hover
                  :title="resolveScaleImageAction('height').tooltip"
                  @click="onChange(resolveScaleImageAction('height').action, 'height')"
                >
                  <img
                    style="transform: rotate(90deg);"
                    src="/img/icons/expand.svg"
                  >
                </button>
                <button
                  v-if="options.isSelectImageFromBackground"
                  v-b-tooltip.hover
                  title="Scale to template size"
                  @click="handleScaleToTemplateSize"
                >
                  <img
                    src="/img/icons/full-screen.svg"
                  >
                </button>
                <button
                  v-if="options.isConvertibleToSmartObject"
                  v-b-tooltip.hover
                  title="Convert to SmartObject"
                  @click="onChange('convertToSmartObject')"
                >
                  <img
                    src="/img/icons/smart.svg"
                    alt="convert-to-smart-object-icon"
                  >
                </button>
                <button
                  v-b-tooltip.hover
                  :title="resolveCenterImageAction.tooltip"
                  @click="onChange(resolveCenterImageAction.action, resolveCenterImageAction)"
                >
                  <img
                    src="/img/icons/center-image.svg"
                    alt="center-icon"
                  >
                </button>
                <button
                  v-b-tooltip.hover
                  :title="resolveHorizontalCenterImageAction.tooltip"
                  @click="onChange(resolveHorizontalCenterImageAction.action, resolveCenterImageAction)"
                >
                  <img
                    src="/img/icons/align-center.png"
                    alt="align-center-icon"
                  >
                </button>
                <button
                  v-b-tooltip.hover
                  :title="resolveVerticalCenterImageAction.tooltip"
                  @click="onChange(resolveVerticalCenterImageAction.action, resolveCenterImageAction)"
                >
                  <img
                    src="/img/icons/vertical-alignment.png"
                    alt="align-center-icon"
                  >
                </button>
                <button
                  v-b-tooltip.hover
                  class="flip-x"
                  title="Flip horizontal"
                  @click="onChange('flipX')"
                >
                  <img src="/img/icons/horizontal-flip.svg">
                </button>
                <button
                  v-b-tooltip.hover
                  class="flip-y"
                  title="Flip vertical"
                  @click="onChange('flipY')"
                >
                  <img src="/img/icons/vertical-flip.svg">
                </button>
                <button
                  v-if="!options.isStaticImage"
                  v-b-tooltip.hover
                  class="crop"
                  title="Crop"
                  @click="handleCrop"
                >
                  <svg
                    class="x-svg"
                    width="22"
                    height="22"
                  >
                    <use href="/img/icons/sprite.svg#crop" />
                  </svg>
                </button>
                <button
                  v-if="target.originalPathForRestoreAfterCrop"
                  v-b-tooltip.hover
                  class="restore-original undo-button"
                  title="Restore original image (crop)"
                  @click="restoreOriginalAfterCrop"
                >
                  <img
                    src="/img/icons/reset.svg"
                    alt="restore"
                  >
                </button>
                <button
                  v-if="isBgThemeImage"
                  v-b-tooltip.hover
                  class="remove-gb"
                  title="Remove background"
                  @click="handleRemoveBackground"
                >
                  <img src="/img/icons/remove-bg.svg">
                </button>
                <button
                  v-if="isFileImage"
                  v-b-tooltip.hover
                  class="remove-gb"
                  title="Remove background"
                  @click="showProductImageRemoveBGModal"
                >
                  <img src="/img/icons/remove-bg.svg">
                </button>
                <button
                  v-b-tooltip.hover
                  title="Reset aspect ratio"
                  @click="onChange('resetAspectRatio')"
                >
                  <img src="/img/icons/reset-aspect-ratio.svg">
                </button>
                <button
                  v-b-tooltip.hover.viewport.noninteractive="'Reset to original dimensions'"
                  @click="onChange('resetToOriginalDimensions')"
                >
                  <img src="/img/icons/reset-to-original-dimensions.svg">
                </button>
              </div>
            </div>
          </div>
        </b-collapse>
      </transition>
    </div>
    <div class="position-fixed apply-to-image-dropdwon-section">
      <ApplyToAllImageDropdown
        :target="target"
      />
    </div>
    <div class="d-flex flex-wrap gap-2 mb-1">
      <b-button
        variant="danger"
        @click="onChange('deleteConfirmation')"
      >
        <b-icon icon="trash" />
        Delete Layer
      </b-button>
      <button
        v-b-tooltip.hover="'Mask'"
        :data-id="selectedInstance.canvas.instance_id"
        class="btn btn-primary mask-settings"
      >
        <b-icon icon="mask" />
      </button>

      <LockToggle />
      <SeeThrough />
      <RestoreSeeThrough />
    </div>

    <ProductImageRemoveBGModal
      ref="productImageRemoveBGModal"
      @replaceExistingImage="replaceImage"
      @copyAndReplaceImage="copyImage"
    />
  </div>
</template>

<script>
import { editorControlEventBroker, EVENTS } from '../../../../group/modules/editor-control-popup/event-broker';
import ProductImageDropdown from './components/ProductImageDropdown.vue';
import { LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_IMAGE } from '../../../../group/modules/editor-control-popup/selected-template-for-apply-to-all';
import CropImage from './components/CropImage.vue';
import ColorPicker from './components/ColorPicker.vue';
import PositionAndSize from './components/PositionAndSize.vue'
import { mapGetters } from 'vuex'
import LockToggle from '@frontend/components/modules/layouts-page/EditorControl/components/LockToggle.vue'
import SeeThrough from '@frontend/components/modules/layouts-page/EditorControl/components/SeeThrough.vue'
import RestoreSeeThrough from '@frontend/components/modules/layouts-page/EditorControl/components/RestoreSeeThrough.vue'
import ApplyToAllImageDropdown from '@frontend/components/modules/layouts-page/EditorControl/components/ApplyToAllImageDropdown.vue';
import { DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES } from '@frontend/group/modules/additional-fields'
import FileUploadService from '@frontend/services/api/file-upload';
import {
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID, 
  ADDITIONAL_HTML_TABLE_PREFIX_ID,
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  IMAGE_PREFIX_ID, MASK_PREFIX_ID
} from "@frontend/constants/templates-field-prefixes";
import { GLOBAL_UPDATE_LOCAL_STORAGE_KEY } from "@frontend/constants/global-update";
import ObjectFillFit from "@frontend/components/modules/layouts-page/EditorControl/components/ObjectFillFit.vue";
import { parseJsonData } from '@frontend/services/helpers';
import { initDataBuilderTable } from '@frontend/group/modules/simple-table-builder/helpers';
import ProductImageRemoveBGModal from '../../file-browser/modals/ProductImageRemoveBGModal.vue';
import { RELATED_FIELD_TYPES } from "@frontend/constants/related-fields";
import Swal from 'sweetalert2';
import warningMixin from '@frontend/mixins/warningMixin';
import PositionWarning from '@frontend/components/shared/PositionWarning.vue'
import { simpleTableBuilderEventBroker, EVENTS as TABLE_EVENTS } from "@frontend/group/modules/simple-table-builder/event-broker";
import {
  EDIT_IMAGE_EVENTS,
  editImageEventBroker,
} from "@frontend/group/modules/edit-image/event-broker";
import { deleteURLParameters, setURLParameters } from "@frontend/utils";
import Multiselect from 'vue-multiselect'
import { editModeService } from "@frontend/services/EditModeService";

const DEBOUNCE_UPDATE_TIME = 50;

const IMAGE_EDITOR_ACTIONS = {
  SET_TEMPLATE_SIZE_BY_WIDTH_OR_HEIGHT: 'setTemplateSizeByWidthOrHeight',
  SET_BY_SMART_OBJECT_WIDTH_OF_HEIGHT: 'setBySmartObjectWidthOrHeight',
  CENTER_IMAGE: 'centerImage',
  HORIZONTAL_CENTER: 'horizontalCenter',
  VERTICAL_CENTER: 'verticalCenter',
}

const SCALE_ACTIONS_MAP = {
  [IMAGE_EDITOR_ACTIONS.SET_TEMPLATE_SIZE_BY_WIDTH_OR_HEIGHT]: {
    width: 'Set to template width',
    height: 'Set to template height',
    default: 'Scale to template size (covers full template)'
  },
  [IMAGE_EDITOR_ACTIONS.SET_BY_SMART_OBJECT_WIDTH_OF_HEIGHT]: {
    width: 'Set to SmartObject width',
    height: 'Set to SmartObject height',
    default: 'Scale to SmartObject size (covers full SmartObject)'
  }
}

const BG_THEME_PREFIXES = [
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID
];

const CENTER_ACTIONS_MAP = {
  CENTER_BY_TEMPLATE_DIMENSIONS: 'Center to template size',
  CENTER_BY_SMART_OBJECT_DIMENSIONS: 'Center to SmartObject size',
  CENTER_HORIZONTALLY: 'Center Horizontally',
  CENTER_VERTICALLY: 'Center Vertically'
}

const PSD_FILE_TYPE = 'image/vnd.adobe.photoshop';

export default {
  components: {
    Multiselect,
    ObjectFillFit,
    ApplyToAllImageDropdown,
    LockToggle,
    SeeThrough,
    RestoreSeeThrough,
    PositionAndSize,
    ProductImageDropdown,
    CropImage,
    ColorPicker,
    ProductImageRemoveBGModal,
    PositionWarning
  },
  mixins: [warningMixin],
  props: {
    options: {
      type: Object,
      default: () => {},
    },
    target: {
      type: fabric.Object,
      default: () => {}
    },
    isTabActive: {
      type: Boolean,
    },
    themes: {
      type: Array,
      default: () => [],
    },
    isOpen: {
      type: Object,
      default: () => ({
        themeSection: true,
        borderSection: true,
        opacityAndScaleSection: true,
        actionSection: true,
        productSection: true,
        productSettingsSection: true,
        imageSettingsSection: true,
        outlineSettingsSection: true,
      }),
    },
  },
  data() {
    return {
      LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_IMAGE,
      maxStrokeWidth: 100,
      isCropMode: false,
      objectInput: {
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        fileIds: '',
        isAutoPosition: false,
        isApplyToAll: false,
        fileOutlineId: '',
        fileOutlineColor: '#000000',
      },
      selectedTheme: null,
      opacity: null,
      scale: null,
      themesOptions: [],
      isCopyAndReplace: false,
      isBGCropToImage: false,
      localStrokeWidth: 0,
      isClippingMask: false,
    }
  },
  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
    ]),
    selectedThemeObj: {
      get () {
        return _.find(this.cThemesOptions, ['id', this.selectedInstance.data.theme])
      },
      set (theme) {
        this.selectedTheme = theme?.id
      }
    },
    isScaleDisabled() {
      if (!this.target) {
        return false;
      }

      if (this.target.id.startsWith(BACKGROUND_THEME_IMAGE_PREFIX_ID) || this.target.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID)) {
        const objectFitSettings = this.target.objectFitSettings ?? {};
        const objectFillSettings = this.target.objectFillSettings ?? {};

        return Boolean(objectFitSettings.align_to_fit) || Boolean(objectFillSettings.align_to_fill);
      }

      return false;
    },
    outlineData() {
      return {
        productId: this.objectInput.fileOutlineId,
        color: this.objectInput.fileOutlineColor
      }
    },
    isSmartObject() {
      return !!this.selectedInstance.canvas.getActiveObject()?.groupName;
    },
    getSmartObject() {
      const groupName = this.selectedInstance.canvas.getActiveObject()?.groupName;

      if (!groupName) {
        return null;
      }

      return this.selectedInstance.canvas
        .getObjects()
        .find(obj => obj.groupName === groupName && obj.id?.startsWith(MASK_PREFIX_ID));
    },
    iconClass() {
      return this.isOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    resolveCenterImageAction() {
      let tooltip =  CENTER_ACTIONS_MAP.CENTER_BY_TEMPLATE_DIMENSIONS;
      let centerPoint = this.selectedInstance.canvas.getCenter();
      const smartObject = this.getSmartObject;

      if (smartObject) {
        tooltip = CENTER_ACTIONS_MAP.CENTER_BY_SMART_OBJECT_DIMENSIONS;
        centerPoint = smartObject.getCenterPoint();
      }

      return {
        action: IMAGE_EDITOR_ACTIONS.CENTER_IMAGE,
        tooltip,
        centerPoint,
        isSmartObject: !!smartObject
      }
    },
    resolveHorizontalCenterImageAction() {
      let tooltip =  CENTER_ACTIONS_MAP.CENTER_HORIZONTALLY;
      let centerPoint = this.selectedInstance.canvas.getCenter();
      const smartObject = this.getSmartObject;

      if (smartObject) {
        centerPoint = smartObject.getCenterPoint();
      }

      return {
        action: IMAGE_EDITOR_ACTIONS.HORIZONTAL_CENTER,
        tooltip,
        centerPoint,
        isSmartObject: !!smartObject
      }
    },
    resolveVerticalCenterImageAction() {
      let tooltip =  CENTER_ACTIONS_MAP.CENTER_VERTICALLY;
      let centerPoint = this.selectedInstance.canvas.getCenter();
      const smartObject = this.getSmartObject;

      if (smartObject) {
        centerPoint = smartObject.getCenterPoint();
      }

      return {
        action: IMAGE_EDITOR_ACTIONS.VERTICAL_CENTER,
        tooltip,
        centerPoint,
        isSmartObject: !!smartObject
      }
    },
    cThemesOptions() {
      return [...this.themesOptions];
    },
    imageSrc () {
      let url = deleteURLParameters(this.target?.getSrc(), ['lightweight', 'full_resolution', 'variant']);
      url = setURLParameters(url, { full_resolution: true });

      return url;
    },
    imagePreviewSrc () {
      const src = this.target?.getSrc();
      if (!src) return '';
      let url = deleteURLParameters(src, ['lightweight', 'full_resolution', 'scale', 'format']);
      url = setURLParameters(url, { lightweight: 1, scale: 120, format: 'webp' });
      return url;
    },
    imageIsDefault () {
      return this.imageSrc?.includes(DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES['additional_background_images'])
    },
    isBgThemeImage() {
      return BG_THEME_PREFIXES.some(prefix => this.target.id.startsWith(prefix));
    },
    isFileImage() {
      return this.target.id.startsWith(IMAGE_PREFIX_ID);
    },
    isRelatedFieldChangeAvailable() {
      return this.target.fieldType === 'HTML Table' || this.options.isBackgroundThemeImage || this.isBgThemeImage;
    },
    relatedTemplateFields() {
      const fields = this.selectedInstance.template.fields
        .filter(field =>  RELATED_FIELD_TYPES.includes(field.type) && field.name !== this.target.name)
        .map(field => ({ value: field.name, text: field.name }));
      
      return [
        { value: '', text: 'Select related field' },
        ...fields
      ];
    },
    percentOfTemplate() {
      return (
        (this.target.getScaledWidth() * this.target.getScaledHeight()) / (this.target.canvas.width * this.target.canvas.height) * 100
      ).toFixed(1);
    },
    isEditingDisabled() {
      return !editModeService.allowsContentEditing(this.target);
    },
    isTransformDisabled() {
      return !editModeService.allowsStructuralChanges(this.target);
    },
  },
  watch: {
    ...(
      [
        'target.left',
        'target.top',
        'target.width',
        'target.height',
        // Resizing an HTML table scales the image (scaleX/scaleY) without
        // touching its natural width/height, so watch the scale too — otherwise
        // changing one of W/H wouldn't refresh the other field in the panel.
        'target.scaleX',
        'target.scaleY',
        'target.fileName',
        'target.fileOutlineId',
        'target.color',
        'target.isClippingMask'
      ].reduce(
        (acc, key) => {
          acc[key] = _.debounce(function () {
            this.initTargetData();
          }, DEBOUNCE_UPDATE_TIME)

          return acc;
        },
        {}
      )
    ),
    'target.strokeWidth': {
      immediate: true,
      handler(newVal) {
        this.localStrokeWidth = newVal;
      }
    }
  },
  mounted() {
    this.initTargetData();
    this.getThemesOptions();
  },
  methods: {
    async getThemesOptions() {
      const {data} = await axios.get(
        window.location.origin 
        + `/banner/get-template-themes?instance_id=${this.target.canvas.instance_id}` 
        + `&layout_id=${$("#template-group-preview").attr("data-layout-id")}`
      );
      this.themesOptions = data;
    },
    restoreOriginalAfterCrop() {
      console.log('restoreOriginalAfterCrop')
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: 'undo_all',
        value: {
          target: this.target,
        },
      })
    },
    toggleSectionHandler(sectionKey) {
      this.$emit('toggleSection', sectionKey);
    },
    initTargetData() {
      const { x, y } = this.target.getPointByOrigin('left', 'top');

      this.objectInput.left = x.toFixed(2);
      this.objectInput.top = y.toFixed(2);
      this.objectInput.width = this.target.getScaledWidth().toFixed(2);
      this.objectInput.height = this.target.getScaledHeight().toFixed(2);

      if (this.target.group) {
        const matring = [...this.target.calcTransformMatrix()]

        this.objectInput.top = (matring.pop() - this.objectInput.height / 2).toFixed(2)
        this.objectInput.left = (matring.pop() - this.objectInput.width / 2).toFixed(2)
      }

      this.objectInput.isAutoPosition = !!this.target.isAutoPosition;
      this.objectInput.fileIds = this.target.fileName;
      this.objectInput.isApplyToAll = this.target.isApplyToAll;

      this.selectedTheme = this.selectedInstance.data.theme;

      // Get current image path and extract theme
      const currentSrc = this.target.getSrc();
      if (currentSrc) {
        const urlParams = new URLSearchParams(currentSrc.split('?')[1]);
        const filePath = urlParams.get('file');
        
        if (filePath && filePath.includes('files/background')) {
          const pathParts = filePath.split('/');
          const themeId = pathParts[4]; // Get themeId from URL structure
          
          if (themeId && Number.isInteger(parseInt(themeId))) {
            this.selectedTheme = themeId;
          }
        }
      }

      this.objectInput.fileOutlineId = this.target.fileOutlineId;
      this.objectInput.fileOutlineColor = this.target.color;
      this.opacity = this.target.opacity ?? 1
      this.scale = parseFloat(this.target.scaleX ?? 1).toFixed(3)
      this.isClippingMask = this.target.isClippingMask;
    },
    onChange(action, value) {
      if (this.target?.fieldType && this.target?.fieldType === 'HTML Table' && ['w', 'h'].includes(action)) {
        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
          action: 'htmlTableImageChanged',
          baseAction: action,
          value
        })
        return
      }
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action, value
      })
    },
    resolveScaleImageAction(scaleParam = null) {
      const smartObject = this.getSmartObject;
      const action = smartObject
        ? IMAGE_EDITOR_ACTIONS.SET_BY_SMART_OBJECT_WIDTH_OF_HEIGHT
        : IMAGE_EDITOR_ACTIONS.SET_TEMPLATE_SIZE_BY_WIDTH_OR_HEIGHT;

      if (!scaleParam) {
        const canvas = action === IMAGE_EDITOR_ACTIONS.SET_TEMPLATE_SIZE_BY_WIDTH_OR_HEIGHT
          ? this.selectedInstance.canvas
          : smartObject;

        const widthScale = canvas.width / this.target.getScaledWidth();
        const heightScale = canvas.height / this.target.getScaledHeight();
        
        if (widthScale > heightScale) {
          scaleParam = 'width';
        } else {
          scaleParam = 'height';
        }
      }

      return {
        action,
        tooltip: SCALE_ACTIONS_MAP[action][scaleParam],
        scaleParam
      }
    },
    handleScaleToTemplateSize() {
      const smartObject = this.getSmartObject;
      const action = smartObject
        ? IMAGE_EDITOR_ACTIONS.SET_BY_SMART_OBJECT_WIDTH_OF_HEIGHT
        : IMAGE_EDITOR_ACTIONS.SET_TEMPLATE_SIZE_BY_WIDTH_OR_HEIGHT;

      const scaleParam = this.resolveScaleImageAction(null).scaleParam;

      this.onChange(action, scaleParam);
    },
    async handleRemoveBackground(isCropToImage = false) {
      if (this.isBgThemeImage) {
        const confirm = await Swal.fire({
          title: "Would you like to crop the image?",
          icon: "question",
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Yes, crop it!",
          denyButtonText: "No, don't crop it!",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#209c86",
          denyButtonColor: "#209c86",
        });

        if (confirm.isConfirmed) {
          this.isBGCropToImage = true;
        } else if (confirm.isDenied) {
          this.isBGCropToImage = false;
        } else {
          return;
        }
      }

      try {
        toastr.info(
          `<div class="d-inline-flex align-content-center">Removing background...<div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden"></span></div></div>`,
          null,
          {
            timeOut: 0,
            tapToDismiss: false,
            closeButton: false
          })

        const src = this.target.getSrc();
        let filePath = null;
        try {
          const url = new URL(src, window.location.origin);
          filePath = url.searchParams.get('file');

          // Fallback: for images served from /storage/ (e.g. segmentation layers),
          // extract the path after /storage/ to get the storage-relative path
          if (!filePath && url.pathname.startsWith('/storage/')) {
            filePath = url.pathname.substring('/storage/'.length);
          }
        } catch {
          // URL parsing failed
        }

        if (!filePath) {
          toastr.error('Could not resolve the image path for background removal');
          return;
        }

        const el = document.getElementById('template-group-preview')
        const elCompanySelect = document.getElementById('company_selector')
        const company_id = elCompanySelect?.value;
        let customerId = el?.getAttribute('data-customer-id');

        const { data } = await FileUploadService.removeImageBackground({
          filePath: filePath,
          template_id: this.selectedInstance.template.id,
          customer_id: customerId,
          company_id: company_id,
          is_crop_to_image: this.isBGCropToImage
        });

        if (!data.imgUrl) {
          toastr.error('Background removal failed — no image was returned');
          return;
        }

        if (this.isFileImage) {
          this.onChange('removeBackgroundProductImage', {
            imgUrl: data.imgUrl,
            filePath: filePath,
            target: this.target,
            isCopyAndReplace: this.isCopyAndReplace,
            instanceId: this.selectedInstance.id,
            isCropToImage
          });
        } else {
          this.onChange('removeBackgroundThemeImgBG', {
            imgUrl: `/share?file=${data.imgUrl}`,
            objectId: this.target.id,
            instanceId: this.selectedInstance.id,
            target: this.target
          });
        }

        toastr.clear();
      } catch (error) {
        console.log(error);
        toastr.clear();
        toastr.error('Something went wrong while removing background');
      }
    },
    handleCrop() {
      this.isCropMode = true;
      this.$refs.cropImage.scrollIntoView({ behavior: "smooth" })
    },
    isPSDFileType(file) {
      return file.type === PSD_FILE_TYPE
        || file.name.split('.').pop().toLowerCase() === 'psb'
        || file.name.split('.').pop().toLowerCase() === 'psd';
    },
    async onDrop(event) {
      if (this.options.isSelectImageFromBackground) {
        this.onImageChange('selectBackground');
        const droppedFiles = event.dataTransfer.files;
        const $newBackgroundImage = $('#new-background-image');
        const $buttonSelect = $("#selectBkImgModal #submit");
        const $compressCheckbox = $("#compress_check")
        const $fileCaption = $newBackgroundImage.closest(".input-group").find(".file-caption-name")

        $newBackgroundImage.fileinput('readFiles', droppedFiles);
        $newBackgroundImage[0].files = droppedFiles;

        $fileCaption.val(`${droppedFiles.length} file selected`);
        $fileCaption.addClass("is-valid");

        if (droppedFiles.length) {
          droppedFiles?.length && this.isPSDFileType(droppedFiles[0])
            ? $compressCheckbox.prop('checked', false)
            : $compressCheckbox.prop('checked', true);

          $buttonSelect.hasClass('active')
            ? $buttonSelect.removeClass('active')
            : $buttonSelect.addClass('active');

          window.startSelectImageUpload?.(droppedFiles);
        }
      }

      localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, false);

      this.$root.$emit('productImageReplace', event)

      $('.b-sidebar').css({ border: 'none' });
    },
    onDragEnter() {
      $('.b-sidebar').css({ border: '1px solid blue' });
    },
    onDragLeave(e, forcibly = false) {
      $('.b-sidebar').css({ border: "none" });
    },
    onDragOver() {
      $('.b-sidebar').css({ border: "1px solid blue" });
    },
    onImageChange(action, value) {
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action, value
      })
    },
    initTableEditor() {
      const data = this.target.html_table_data.find((data) => {
        if (data && data.trim()) {
          const table = parseJsonData(data, null);
          return table.id === this.target.field_id;
        }
      });
      
      const table = initDataBuilderTable(parseJsonData(data, null));

      // Tables render at their natural (content-driven) size, so open the builder at the table's
      // own stored size rather than pinning it to the on-canvas display size.
      simpleTableBuilderEventBroker.fire(TABLE_EVENTS.INIT, {
        table,
        params: this.options.params,
        tableData: this.options.tableData,
      });
     
    },
    replaceImage(isCropToImage) {
      this.isCopyAndReplace = false;

      this.handleRemoveBackground(isCropToImage);
    },
    copyImage(isCropToImage) {
      this.isCopyAndReplace = true;

      this.handleRemoveBackground(isCropToImage);
    },
    showProductImageRemoveBGModal() {
      this.$refs.productImageRemoveBGModal.show();
    },
    showImageEditorModal() {
      const filePath = new URL(this.imageSrc).searchParams.get('file');
      if (filePath) {
        const splitFilePath = filePath.split('/');
        const fileName = splitFilePath[splitFilePath.length -1];
        this.onChange('selectBackground', {
          templateId: this.selectedInstance.template.id,
          path: filePath,
          name: fileName,
          target: this.target,
          needOpenImageEditor: true
        })
      }
      if (!this.selectedTheme) {
        toastr.warning('Theme is not selected');
        toastr.clear();
      }
    },
    toggleProductDropdown() {
      const dropdown = this.$refs.productImageDropdown
      if (!dropdown) return
      dropdown.onFocusProductIds(!dropdown.isShowDropdown)
    },
    handleAutoCrop({ imgUrl, filePath, isCopyAndReplace, isCropToImage, fileName }) {
      if (this.isFileImage) {
        this.onChange('removeBackgroundProductImage', {
          imgUrl,
          filePath,
          target: this.target,
          isCopyAndReplace,
          instanceId: this.selectedInstance.id,
          isCropToImage,
          isFromCropping: true
        });
      } else {
        this.onChange('selectBackground', {
          changeUploadText: true,
        });

        toastr.info(
          `<div class="d-inline-flex align-content-center">Processing image...<div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden"></span></div></div>`,
          null,
          {
            timeOut: 10000,
            tapToDismiss: false,
            closeButton: false
          }
        );

        const properImgUrl = imgUrl.startsWith('http') ? imgUrl : `/share?file=${imgUrl}`;
        
        fetch(properImgUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error('Network response was not ok');
            }
            return response.blob();
          })
          .then(blob => {
            const now = new Date();
            const timestamp = now.toISOString().replace(/[:.]/g, "-");
            const filename = fileName ? fileName : `image-${timestamp}.png`;
            const file = new File([blob], filename, { type: 'image/png' });

            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);

            const $newBackgroundImage = $("#new-background-image");
            const $buttonSelect = $("#selectBkImgModal #submit");
            const $compressCheckbox = $("#compress_check");
            const $fileCaption = $newBackgroundImage
              .closest(".input-group")
              .find(".file-caption-name");

            $newBackgroundImage.fileinput("readFiles", dataTransfer.files);
            $newBackgroundImage[0].files = dataTransfer.files;

            $fileCaption.val(`${dataTransfer.files.length} file selected`);
            $fileCaption.addClass("is-valid");

            if (dataTransfer.files.length) {
              $compressCheckbox.prop("checked", true);

              $buttonSelect.hasClass("active")
                ? $buttonSelect.removeClass("active")
                : $buttonSelect.addClass("active");

              window.startSelectImageUpload?.(dataTransfer.files);
            }

            localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, false);

            this.$root.$emit("productImageReplace", {
              dataTransfer,
            });
            
            this.onChange('removeBackgroundThemeImgBG', {
              imgUrl: `/share?file=${imgUrl}`,
              objectId: this.target.id,
              instanceId: this.selectedInstance.id,
              target: this.target,
              isFromCropping: true
            });
            
            toastr.clear();
          })
          .catch(error => {
            console.error('Error fetching image:', error);
            toastr.clear();
            toastr.error('Failed to process the image. Please try again.');
            
            this.onChange('removeBackgroundThemeImgBG', {
              imgUrl: `/share?file=${imgUrl}`,
              objectId: this.target.id,
              instanceId: this.selectedInstance.id,
              target: this.target,
              isFromCropping: true
            });
          });
      }
    }
  }
}
</script>

<style scoped lang="scss">
$image-preview-height: 38px;

.img-preview {
  max-height: $image-preview-height;
}

.download-image {
  position: relative;
  // The download and edit buttons are stacked down the left padding. The box is
  // otherwise only as tall as the preview inside it, so a wide, short image - a line
  // of text cut out of a layer - left the edit button hanging outside the border.
  // The floor keeps the box tall enough for both whatever the image's shape.
  min-height: $image-preview-height;
  display: inline-flex;
  // Shares the line with the Replace and product buttons, which Bootstrap aligns to
  // the middle. On the default baseline the box hangs well above them: the preview
  // image is its only in-flow child, so the box's baseline is that image's bottom edge.
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  text-align: center;
  padding: 0 22px;
  border: 1px solid #1c7964;
  background: white;
  box-sizing: border-box;
  border-radius: 10px;
}

.download-image:hover {
  text-decoration: none;
}

.download-image-href {
  position: absolute;
  top: 0;
  left: 3px;
  opacity: 1;
}

.edit-image-btn {
  position: absolute;
  // Anchored to the bottom rather than a fixed offset from the top: the icon then
  // cannot spill past the border however short the box ends up.
  bottom: 0;
  left: 3px;
  opacity: 1;
  border: 0;
  background: transparent;
  padding: 0;
  color: #209c86;
}

.close-btn {
  position: absolute;
  top: 3px;
  right: 3px;
  background: rgba(255, 255, 255, 0.92);
  color: #6c757d;
  border: 1px solid #dee2e6;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.close-btn:hover {
  color: #dc3545;
  border-color: #dc3545;
  background: #fff;
}

.position-warning {
  font-size: 0.9rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  
  .warning-actions {
    .btn-sm {
      display: flex !important;
      align-items: center !important;
      min-width: auto;
      padding: 0.25rem 0.75rem;
      color: #000 !important;
    }
  }
}

/* Optional smoother collapse animation */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
}
.collapse-enter,
.collapse-leave-to {
  opacity: 0;
  height: 0;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

.apply-to-image-dropdwon-section{
  bottom: 44px;
  z-index: 999;
}
</style>
